// Portal auth context — cookie-session, role-aware.
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { identifyUser } from "../components/Analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/portal`;

// Always send cookies
axios.defaults.withCredentials = true;

const PortalAuthContext = createContext(null);

export function PortalAuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = signed-in
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      if (data?.email) identifyUser({ email: data.email, role: data.role });
    } catch {
      setUser(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(data);
    if (data?.email) identifyUser({ email: data.email, role: data.role });
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) { /* ignore */ }
    setUser(false);
  }, []);

  return (
    <PortalAuthContext.Provider value={{ user, checking, login, logout, refresh }}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export const usePortalAuth = () => useContext(PortalAuthContext);

export function ProtectedPortalRoute({ children, roles }) {
  const { user, checking } = usePortalAuth();
  const location = useLocation();
  if (checking) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="overline text-ink/55 animate-pulse">Loading the room…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/portal" state={{ from: location }} replace />;
  if (roles && roles.length && !roles.includes(user.role)) {
    return <Navigate to="/portal/dashboard" replace />;
  }
  return children;
}

// Studio-buyer flavour: same auth under the hood, but bounces unauth users to
// /library/login (the Studio's OWN door) instead of /portal (which is for the
// internal team + retainer clients only).
export function ProtectedLibraryRoute({ children }) {
  const { user, checking } = usePortalAuth();
  const location = useLocation();
  if (checking) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="overline text-ink/55 animate-pulse">Opening your shelf…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/library/login" state={{ from: location }} replace />;
  return children;
}

// Helper for nice error messages from FastAPI
export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

export { API as PORTAL_API };
